import { StrictMode, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";

import { createHttpTransport } from "@tdelivr/api-client";

import { arrivalFor, LeavingForTheProvider } from "./auth/arrival.tsx";
import { SessionAuthority, type SignInFailure } from "./auth/authority.ts";
import { isConfigured, readOidcConfiguration } from "./auth/configuration.ts";
import { useAuthentication } from "./auth/use-authentication.ts";
import { developmentSession } from "./development-session.ts";
import { backFrom } from "./navigation.ts";
import {
  identifierIn,
  pageIdIn,
  placeIn,
  placeParameter,
  textIn,
  urlFor,
  urlForPage,
  type Place,
} from "./page-location.ts";
import { createRiderApi } from "./rider-api.ts";
import type { Session } from "./session.ts";
import { Cart } from "./ui/Cart.tsx";
import { Checkout } from "./ui/Checkout.tsx";
import { ChooseRide } from "./ui/ChooseRide.tsx";
import { Confirming } from "./ui/Confirming.tsx";
import { Consent } from "./ui/Consent.tsx";
import { Finding, type SearchPageId } from "./ui/Finding.tsx";
import { Merchants } from "./ui/Merchants.tsx";
import { MultiStopInTrip } from "./ui/MultiStopInTrip.tsx";
import { OrderTracking } from "./ui/OrderTracking.tsx";
import { PaymentMethods } from "./ui/PaymentMethods.tsx";
import { PaymentOutcome, type PaymentPageId } from "./ui/PaymentOutcome.tsx";
import { Products } from "./ui/Products.tsx";
import { Quote } from "./ui/Quote.tsx";
import { Refund } from "./ui/Refund.tsx";
import { Ride } from "./ui/Ride.tsx";
import { ScreenOutlook } from "./ui/ScreenOutlook.tsx";
import { SessionEnded } from "./ui/SessionEnded.tsx";
import { OrderHistory } from "./ui/OrderHistory.tsx";
import { Settings } from "./ui/Settings.tsx";
import { SignIn } from "./ui/SignIn.tsx";
import { StateScreen } from "./ui/StateScreen.tsx";
import { Wallet, type WalletPageId } from "./ui/Wallet.tsx";
import { WhereTo, type DevicePosition } from "./ui/WhereTo.tsx";
import "./styles.css";

/**
 * The rider application, running.
 *
 * Two ways in, and only one of them exists in a production build.
 *
 * **Sign in.** OpenID Connect, authorization code flow with PKCE, against the provider named by
 * `VITE_OIDC_ISSUER`. The shape is not a choice: a browser application cannot hold a client secret,
 * so a public client with PKCE is the only correct option. An unauthenticated arrival does not draw
 * a form — it goes straight to the provider's own page, per ADR 0022 — and `auth/arrival.tsx` holds
 * the decision, including the arrivals that must **not** be redirected because redirecting them is a
 * loop.
 *
 * **Development claims.** `VITE_ACCESS_TOKEN_CLAIMS`, read by `development-session.ts`, which is the
 * path the acceptance check and the tests run on. Compiled out of a production build rather than
 * merely discouraged in it — see that file for the two mechanisms.
 *
 * ## There is no shell, because the design draws none
 *
 * Both consoles have a rail, a top bar, breadcrumbs, a help popover, a notifications bell and an
 * account menu, because `CLAUDE.md` section 3's header kit applies to **every console page**.
 * `10 Rider App.dc.html` draws none of it. What it draws instead is an app bar **inside** each
 * screen — a back control, the CityCab mark and the person-or-SOS control — and that belongs to the
 * screen rather than around it, which is why `PhoneShell` owns it and the routing below adds
 * nothing. Eleven screens, each filling the device.
 *
 * ## The address carries the journey, not just the screen
 *
 * Every screen after `1 Where to` is about something with an identifier, and **none of those
 * identifiers is discoverable**. A rider cannot list their own saved places, their own trips or
 * their own quotes; R-28 records that nothing even joins a ride request to the trip created from it.
 * So the route, the quote, the ride request and the trip all live in the address:
 *
 *     ?page=choose&from=<lat,lon>&to=<lat,lon>&toLabel=<text>
 *     ?page=quote&quote=<uuid>
 *     ?page=driver-coming&ride=<uuid>[&trip=<uuid>]
 *
 * That is not a convenience. It is the only place this application can hold them, it is what makes
 * the acceptance check able to put a screen into a named state, and `&trip=` is the exact shape of
 * the gap R-28 names: a rider whose application does not get a trip identifier from somewhere sees
 * the designed absence on three screens.
 */

interface AppProps {
  readonly session: Session;
  readonly accessToken?: () => Promise<string | undefined>;
  /** Rendered in place of the screen when the session has ended. */
  readonly expired?: ReactNode;
  /**
   * Ends this device's session with the identity provider, for `Settings`'s `Sign out` row.
   *
   * Absent in a development build, which has no provider in the loop. The row is still drawn — the
   * design draws it — and says why it cannot work, which is `Settings.tsx`'s own note.
   */
  readonly onSignOut?: () => void;
}

function App({ session, accessToken, expired, onSignOut }: AppProps): ReactNode {
  const [pageId, setPageId] = useState<string>(() => pageIdIn(window.location.href));
  const [address, setAddress] = useState<string>(() => window.location.href);

  useEffect(() => {
    // Back and forward move between screens rather than out of the application, now that screens are
    // addressable.
    const onPopState = (): void => {
      setPageId(pageIdIn(window.location.href));
      setAddress(window.location.href);
    };
    window.addEventListener("popstate", onPopState);
    return (): void => window.removeEventListener("popstate", onPopState);
  }, []);

  /**
   * Move to a screen the application decided on, rather than one the rider asked for.
   *
   * `replaceState`, not `pushState`: a quote that turns out to have expired is a correction of the
   * address rather than a step in a journey, and pushing it would put the screen the rider could not
   * use into their back button.
   */
  const correct = useCallback((next: string) => {
    if (pageIdIn(window.location.href) === next) return;
    window.history.replaceState(null, "", urlForPage(window.location.href, next));
    setPageId(next);
    setAddress(window.location.href);
  }, []);

  /** Move to a screen the rider asked for, carrying the identifiers it is about. */
  const go = useCallback(
    (next: string, parameters: Readonly<Record<string, string | undefined>> = {}) => {
      window.history.pushState(null, "", urlFor(window.location.href, next, parameters));
      setPageId(next);
      setAddress(window.location.href);
    },
    [],
  );

  const back = useCallback(() => {
    const to = backFrom[pageIdIn(window.location.href)];
    if (to === undefined) return;
    go(to);
  }, [go]);

  const transport = useMemo(
    () =>
      createHttpTransport({
        // The page's own origin, not "/". `new URL("v1/…", "/")` throws, so a bare slash would make
        // every request fail — found the hard way on the dispatch console. Same origin is also the
        // right default once the gateway exists: WEB-0743 forbids exposing the services directly, so
        // the application and its API are one origin in every environment that is not a laptop.
        baseUrl: import.meta.env.VITE_PLATFORM_URL ?? window.location.origin,
        tenantId: () => session.tenantId,
        ...(accessToken === undefined ? {} : { authorization: accessToken }),
      }),
    [session.tenantId, accessToken],
  );

  const api = useMemo(() => createRiderApi(transport), [transport]);
  const now = useCallback(() => Date.now(), []);
  const newId = useCallback(() => crypto.randomUUID(), []);
  const position = useDevicePosition();

  const from = useMemo(() => placeIn(address, "from"), [address]);
  const to = useMemo(() => placeIn(address, "to"), [address]);
  const toLabel = useMemo(() => new URL(address).searchParams.get("toLabel"), [address]);
  const quoteId = useMemo(() => identifierIn(address, "quote"), [address]);
  const rideRequestId = useMemo(() => identifierIn(address, "ride"), [address]);
  const tripId = useMemo(() => identifierIn(address, "trip"), [address]);

  /*
   * The second tranche's identifiers, and none of them is discoverable either.
   *
   * The same reasoning as `&ride=` and `&trip=`, repeated because the second tranche repeats the
   * shape five times over: COM-04 publishes no listing of a customer's carts, COM-05 no listing of
   * their orders, FIN-04's refund listing has no payer filter, MER-02 no listing of a merchant's
   * branches, COM-01 no listing of a tenant's catalogues, and PAY-02 no listing of payment
   * executions. So every one of those arrives in the address, the way a push notification or a deep
   * link from a receipt would carry it:
   *
   *     ?page=products&merchant=<uuid>&location=<uuid>&catalog=<uuid>&category=<text>
   *     ?page=cart&cart=<uuid>
   *     ?page=order-tracking&order=<uuid>
   *     ?page=refund&refund=<uuid>
   *     ?page=outcome-unknown&payment=<uuid>
   *
   * Each screen draws its own absence when its identifier is missing, naming the operation that
   * would have to publish a listing — which is more useful than a blank and is the only honest thing
   * to say.
   */
  const cartId = useMemo(() => identifierIn(address, "cart"), [address]);
  const orderId = useMemo(() => identifierIn(address, "order"), [address]);
  const refundId = useMemo(() => identifierIn(address, "refund"), [address]);
  const executionId = useMemo(() => identifierIn(address, "payment"), [address]);
  /*
   * And the stop plan's, for the same reason again: TRP-04 publishes a read by `planId` and no
   * listing, so a rider's own plan is not discoverable from this application.
   *
   *     ?page=multi-stop-in-trip&plan=<uuid>
   */
  const planId = useMemo(() => identifierIn(address, "plan"), [address]);
  /*
   * And the money screens', which is the same shape a third time and the sharpest instance of it.
   *
   * **Two identifiers, neither discoverable, and nothing joins them to each other.** FIN-01 publishes
   * a wallet by `walletId` and no listing of a customer's wallets, so a rider cannot find their own
   * wallet; FIN-03 publishes a balance by `accountId` and nothing anywhere says which ledger account
   * a wallet corresponds to. The second is the worse of the two, because it is what stops FIN-03's
   * public read from being bounded to a person at all — ADR 0041 §4 names it and Q-145 carries it.
   *
   *     ?page=wallet&wallet=<uuid>&account=<uuid>
   *
   * The screen draws its own absence when either is missing, naming both operations that would have
   * to publish something, which is the treatment every other undiscoverable identifier on this
   * application gets.
   */
  const walletId = useMemo(() => identifierIn(address, "wallet"), [address]);
  const accountId = useMemo(() => identifierIn(address, "account"), [address]);
  const merchantId = useMemo(() => identifierIn(address, "merchant"), [address]);
  const locationId = useMemo(() => identifierIn(address, "location"), [address]);
  const catalogId = useMemo(() => identifierIn(address, "catalog"), [address]);
  // Not a uuid: COM-02 types its `categoryId` query parameter as a plain string.
  const categoryId = useMemo(() => textIn(address, "category"), [address]);

  if (expired !== undefined) return expired;

  const chooseDestination = (input: {
    readonly origin: Place;
    readonly destination: Place;
    readonly destinationLabel: string;
  }): void => {
    go("choose", {
      from: placeParameter(input.origin),
      to: placeParameter(input.destination),
      toLabel: input.destinationLabel,
    });
  };

  /*
   * One branch per built screen, which is also how the completion report counts one.
   *
   * `tooling/scripts/report-completion.mjs` reads `pageId === "…"` out of this file, so a screen is
   * counted exactly when a rider can open it — and a screen routed here without being built to its
   * design would raise that figure while making the product worse. The twenty-eight below are the
   * twenty-eight this application draws; every other designed screen falls through to
   * `ScreenOutlook`, which says what it is waiting on.
   *
   * The four that are not here are every screen the measurement grades **blocked**, and each is
   * absent on purpose:
   *
   *   - `Checkout` — COM-05's `POST …/commerce-orders` references a request schema its own contract
   *     never defines, so the one command the screen exists for cannot be composed. It lost one of
   *     its four absences to ADR 0042 and is blocked on the other three.
   *   - `Multi-stop` — TRP-04's plan publishes no fare and no dwell.
   *   - `Order history` — TRP-01's trip listing has no `riderId` filter, COM-05 publishes no `GET`
   *     at all, and DEL-02's has no `senderPartyId` filter, so none of the three row kinds this
   *     rider's own history is made of can be narrowed to them.
   *   - `Support` — SUP-01 and SUP-03 publish their case and its timeline only under
   *     `/internal/v1`, which ADR 0029 refuses to route, and nothing publishes a reply command.
   *
   * The three money screens were on that list until ADR 0041 and ADR 0042, which are the fifth
   * tranche and the first on this application to need a **new record** rather than a route in a new
   * place: no service modelled a stored payment instrument at all, and FIN-01 — which the design's
   * own note names as the source of the balance — has never had one. `Wallet` and
   * `Wallet not authoritative` share one branch and one component, because the design draws them as
   * one screen in two states and the data decides which, the same shape `quote` / `quote-expired`
   * and `cart` / `cart-conflict` already have here.
   *
   * `5 Finding` and `5b No driver` were on that list until ADR 0031. DSP-04 published its process's
   * `status`, `attempt`, `maximumAttempts`, `deadlineAt` and `failureReason` only inside the 202 of
   * an event-ingestion `POST`, behind `dispatch:orchestrate`, so the search state had no source a
   * rider's application could read and routing the two would have put an invented countdown in front
   * of a rider. `GET /v1/tenants/{tenantId}/dispatch-processes/{demandId}` under `dispatch:read`
   * serves every one of those fields, and nothing had to be invented to build either screen.
   *
   * `Multi-stop in trip` left that list the same way and for the same shape of reason. Its single
   * absence was a stop's place, and ADR 0036 made a stop name a GEO-02 address that TRP-04's create
   * command confirms before it writes a plan — so the place is a published read away from a
   * published field, and no new operation and no new route were needed to draw it.
   */
  return pageId === "where-to" ? (
    <WhereTo
      api={api}
      scopes={session.scopes}
      givenName={session.givenName}
      position={position}
      onDestination={chooseDestination}
    />
  ) : pageId === "choose" ? (
    <ChooseRide
      api={api}
      scopes={session.scopes}
      customerId={session.customerId}
      origin={from}
      destination={to}
      routeLabel={toLabel ?? undefined}
      onQuoteChosen={(id) => go("quote", { quote: id })}
      onBack={back}
    />
  ) : pageId === "quote" || pageId === "quote-expired" ? (
    <Quote
      api={api}
      scopes={session.scopes}
      quoteId={quoteId}
      asked={pageId === "quote-expired" ? "quote-expired" : "quote"}
      onScreenChange={correct}
      onBooked={(id) => go("confirming", { ride: id, quote: undefined })}
      onBack={() => go("choose")}
      now={now}
    />
  ) : pageId === "confirming" ? (
    <Confirming
      api={api}
      tenantId={session.tenantId}
      customerId={session.customerId}
      rideRequestId={rideRequestId}
      tripId={tripId}
      onFollowRide={() => go("finding", { ride: rideRequestId })}
      onBack={back}
    />
  ) : pageId === "finding" || pageId === "no-driver" ? (
    <Finding
      api={api}
      scopes={session.scopes}
      tenantId={session.tenantId}
      customerId={session.customerId}
      rideRequestId={rideRequestId}
      asked={pageId satisfies SearchPageId}
      onScreenChange={correct}
      onQuoteChosen={(id) => go("quote", { quote: id, ride: undefined })}
      onBack={back}
      now={now}
    />
  ) : pageId === "driver-coming" ||
    pageId === "in-trip" ||
    pageId === "complete" ||
    pageId === "fare-differs" ? (
    <Ride
      api={api}
      tenantId={session.tenantId}
      customerId={session.customerId}
      scopes={session.scopes}
      rideRequestId={rideRequestId}
      tripId={tripId}
      screen={pageId}
      onBack={back}
      newId={newId}
      now={now}
    />
  ) : pageId === "merchants" ? (
    <Merchants api={api} scopes={session.scopes} />
  ) : pageId === "products" ? (
    <Products
      api={api}
      scopes={session.scopes}
      tenantId={session.tenantId}
      merchantId={merchantId}
      locationId={locationId}
      catalogId={catalogId}
      categoryId={categoryId}
      onBack={back}
    />
  ) : pageId === "cart" || pageId === "cart-conflict" ? (
    <Cart
      api={api}
      scopes={session.scopes}
      tenantId={session.tenantId}
      customerId={session.customerId}
      cartId={cartId}
      onCheckout={() => go("checkout", { cart: cartId })}
      asked={pageId === "cart-conflict" ? "cart-conflict" : "cart"}
      onScreenChange={correct}
      onBack={() => go("products")}
    />
  ) : pageId === "checkout" ? (
    <Checkout
      api={api}
      scopes={session.scopes}
      tenantId={session.tenantId}
      customerId={session.customerId}
      cartId={cartId}
      onPlaced={(placedOrderId) => go("order-tracking", { order: placedOrderId })}
      onBack={() => go("cart", { cart: cartId })}
    />
  ) : pageId === "order-tracking" ? (
    <OrderTracking api={api} scopes={session.scopes} orderId={orderId} onBack={back} />
  ) : pageId === "multi-stop-in-trip" ? (
    <MultiStopInTrip
      api={api}
      scopes={session.scopes}
      tenantId={session.tenantId}
      planId={planId}
      onBack={back}
      newId={newId}
      now={now}
    />
  ) : pageId === "payment-methods" ? (
    <PaymentMethods api={api} scopes={session.scopes} tenantId={session.tenantId} onBack={back} />
  ) : pageId === "wallet" || pageId === "wallet-not-authoritative" ? (
    <Wallet
      api={api}
      scopes={session.scopes}
      tenantId={session.tenantId}
      walletId={walletId}
      accountId={accountId}
      asked={pageId satisfies WalletPageId}
      onScreenChange={correct}
      onBack={back}
    />
  ) : pageId === "refund" ? (
    <Refund api={api} scopes={session.scopes} refundId={refundId} onBack={back} />
  ) : pageId === "consent" ? (
    <Consent
      api={api}
      scopes={session.scopes}
      tenantId={session.tenantId}
      customerId={session.customerId}
      onBack={back}
    />
  ) : pageId === "order-history" ? (
    <OrderHistory api={api} scopes={session.scopes} onBack={back} />
  ) : pageId === "settings" ? (
    <Settings
      api={api}
      scopes={session.scopes}
      customerId={session.customerId}
      displayName={session.displayName}
      {...(onSignOut === undefined ? {} : { onSignOut })}
      onBack={back}
    />
  ) : pageId === "approve-on-phone" ||
    pageId === "outcome-unknown" ||
    pageId === "still-unknown" ||
    pageId === "confirmed-paid" ||
    pageId === "confirmed-not-charged" ? (
    <PaymentOutcome
      api={api}
      scopes={session.scopes}
      tenantId={session.tenantId}
      customerId={session.customerId}
      executionId={executionId}
      asked={pageId satisfies PaymentPageId}
      onScreenChange={correct}
      onBack={back}
    />
  ) : (
    <ScreenOutlook pageId={pageId} />
  );
}

/**
 * Where this phone is, watched rather than read once.
 *
 * The one fact on `1 Where to` that is the device's rather than the platform's: GEO-02 turns a
 * coordinate into an address and no service can produce the coordinate. It is watched because a
 * rider granting the permission from the browser's own prompt must fill the origin field without a
 * reload, and because a fix taken indoors improves as they walk to the kerb.
 *
 * Every failure is `unavailable` with the reason, never a default position. A plausible coordinate
 * would produce a plausible address, and a rider standing somewhere else would be sent to meet a car
 * at it.
 */
function useDevicePosition(): DevicePosition {
  const [position, setPosition] = useState<DevicePosition>({ kind: "locating" });

  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setPosition({ kind: "unavailable", because: "This device has no location" });
      return;
    }
    const watch = navigator.geolocation.watchPosition(
      (fix) => {
        setPosition({
          kind: "at",
          place: { latitude: fix.coords.latitude, longitude: fix.coords.longitude },
          accuracyMetres: Math.round(fix.coords.accuracy),
        });
      },
      (failure) => {
        setPosition({
          kind: "unavailable",
          because:
            failure.code === failure.PERMISSION_DENIED
              ? "Location is switched off"
              : "We can't get a location",
        });
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 },
    );
    return (): void => navigator.geolocation.clearWatch(watch);
  }, []);

  return position;
}

/**
 * Everything above the screens: leaving to sign in, the code exchange, the expiry and the sign-out.
 *
 * One switch over one state. Exactly one of its branches leaves this page — `signed_out`, and only
 * when `arrivalFor` says the arrival has nothing to report. Every other branch draws something, and
 * that asymmetry is the whole of ADR 0022's caution: a state that is unauthenticated *because
 * something went wrong* must be read, not retried, or the application redirects into the same failure
 * for as long as the browser lets it.
 */
function Authenticated({ authority }: { readonly authority: SessionAuthority }): ReactNode {
  const state = useAuthentication(authority);
  const [dismissedFailure, setDismissedFailure] = useState(false);

  const providerName = authority.providerName;
  const accessToken = useCallback(() => authority.accessToken(), [authority]);
  const signIn = useCallback(() => {
    setDismissedFailure(false);
    void authority.beginSignIn();
  }, [authority]);

  switch (state.kind) {
    case "starting":
      return <StateScreen screen="journey" state={{ kind: "loading" }} purpose="Starting up." />;

    case "signed_out":
      return arrivalFor(state).kind === "to_the_provider" ? (
        <LeavingForTheProvider authority={authority} />
      ) : (
        <SignIn
          step="start"
          afterSignOut={state.afterSignOut}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "authenticating":
      return (
        <SignIn
          step="authenticating"
          afterSignOut={false}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "failed":
      return (
        <SignIn
          step={dismissedFailure ? "start" : "failed"}
          afterSignOut={false}
          providerName={providerName}
          failure={state.failure}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "expired":
      return (
        <App
          session={state.session}
          accessToken={accessToken}
          expired={
            <SessionEnded
              endedAt={state.endedAt}
              lastedMs={state.lastedMs}
              onSignInAgain={() => {
                void authority.beginSignIn();
              }}
            />
          }
        />
      );

    case "signed_in":
      return (
        <App
          session={state.session}
          accessToken={accessToken}
          onSignOut={() => {
            // `Settings`s `Sign out · This device` row. The identity provider owns the session, so
            // this ends it there rather than clearing anything locally — IAM-04s revocation is for
            // the *other* devices, and `Settings.tsx` records why this build cannot name its own.
            void authority.signOut();
          }}
        />
      );
  }
}

/** A build with no identity provider configured, said in this application's own failure screen. */
function unconfigured(missing: readonly string[]): SignInFailure {
  return {
    code: "app_not_configured",
    headline: "This app has no identity provider configured",
    detail: `It was built without ${missing.join(" and ")}, so there is nowhere to send you to sign in. Whoever builds or serves this app sets those. Retrying won't change this.`,
    retryable: false,
  };
}

const root = document.getElementById("root");
if (root === null) throw new Error("The rider app has no mount point");

/*
 * Rendered synchronously, in every case.
 *
 * Module scripts run before `DOMContentLoaded`, and the acceptance check asks whether an element is
 * visible immediately after that event. A bootstrap that awaited anything before its first render
 * would have the check conclude the screen was absent. The asynchronous part of signing in happens
 * after the first paint, behind the loading state.
 */
const development = import.meta.env.DEV ? developmentSession() : undefined;

if (development !== undefined) {
  createRoot(root).render(
    <StrictMode>
      <App session={development} />
    </StrictMode>,
  );
} else {
  const configuration = readOidcConfiguration(import.meta.env, window.location.origin);
  if (!isConfigured(configuration)) {
    createRoot(root).render(
      <StrictMode>
        <SignIn
          step="failed"
          afterSignOut={false}
          providerName="your organisation's sign-in"
          failure={unconfigured(configuration.missing)}
          onContinue={() => {}}
          onStartAgain={() => {}}
        />
      </StrictMode>,
    );
  } else {
    const authority = new SessionAuthority({
      configuration,
      fetch: globalThis.fetch.bind(globalThis),
      crypto: globalThis.crypto,
      // Per tab. Two tabs signing in at once must not read each other's code verifier, and a
      // verifier left in `localStorage` would outlive the browser session it belongs to.
      storage: window.sessionStorage,
      currentUrl: (): string => window.location.href,
      replaceUrl: (url: string): void => {
        window.history.replaceState(null, "", url);
      },
      navigate: (url: string): void => {
        window.location.assign(url);
      },
      now: (): number => Date.now(),
    });

    createRoot(root).render(
      <StrictMode>
        <Authenticated authority={authority} />
      </StrictMode>,
    );
    // After the first render, so the loading state is on screen while discovery and the code
    // exchange happen.
    void authority.start();
  }
}
